export const AuthMessageType = {
    TOKEN: 'goodt/ivs.token',
    PROFILE: 'goodt/ivs.profile',
    LOGOUT: 'goodt/ivs.logout'
};

/**
 * @typedef {import("./iframe-tp").AuthMessageIncoming<T>} AuthMessageIncoming<T>
 * @template {AuthMessageTypeValues} T
 *
 * @typedef {import("./iframe-tp").AuthMessageOutgoing<T>} AuthMessageOutgoing<T>
 * @template {AuthMessageTypeValues} T
 */
/**
 * @typedef {import("./iframe-tp").AuthMessageType} AuthMessageType
 * @typedef {import("./iframe-tp").AuthMessageTypeValues} AuthMessageTypeValues
 * @typedef {import("./iframe-tp").updateToken} updateToken
 * @typedef {import("./iframe-tp").getUserProfile} getUserProfile
 * @typedef {import("./iframe-tp").logout} logout
 * @typedef {import("./iframe-tp").setup} setup
 * @typedef {import("./iframe-tp").Observer} Observer
 * @typedef {import("./iframe-tp").Emitter} Emitter
 * @typedef {{ timeout:number, observer:Observer, emitter:Emitter }} EmitOptions
 */

const createPromise = () => {
    /** @type {Function} */
    let resolve;
    /** @type {Function} */
    let reject;
    const promise = new Promise((res, rej) => {
        resolve = res;
        reject = rej;
    });
    return { promise, resolve, reject };
};

/**
 * @param {AuthMessageOutgoing<AuthMessageTypeValues>} msg
 * @param {EmitOptions} options
 */
const emit = (msg, { timeout = 3000, observer, emitter } = {}) => {
    const { promise, resolve, reject } = createPromise();
    let timeoutId;

    /**
     * @param {AuthMessageIncoming<AuthMessageTypeValues>} msgIncoming
     */
    const handler = (msgIncoming = {}) => {
        if (msg.type === msgIncoming.type) {
            resolve(msgIncoming.payload);
            if (timeoutId) {
                clearTimeout(timeoutId);
            }
        }
    };
    timeoutId = setTimeout(() => reject(new Error(`pm "${msg.type}" timeout`)), timeout);
    observer(handler);
    emitter(msg);
    return promise;
};

/** @return {import("./iframe-tp").IFrameApi} */
export const useIFrame = () => {
    let tokenPromise = null;
    let profilePromise = null;
    /** @type {Observer} */
    let observer = (fn) => window.addEventListener('message', (e) => fn(e.data), { once: true });
    /** @type {Emitter} */
    let emitter = (msg) => window.parent.postMessage(msg, '*');

    /** @type {setup} */
    const setup = (options = {}) => {
        observer = options?.observer
            ? options?.observer
            : (fn) => window.addEventListener('message', (e) => fn(e.data), { once: true });
        emitter = options.emitter ? options.emitter : (msg) => window.parent.postMessage(msg, '*');
    };

    /**
     * Requests a new fresh token from facade
     * @type {updateToken}
     */
    const updateToken = () => {
        if (!tokenPromise) {
            /** @type {AuthMessageOutgoing<AuthMessageType['TOKEN']>} */
            const msg = { type: AuthMessageType.TOKEN };
            tokenPromise = emit(msg, { observer, emitter }).catch(() => Promise.resolve(null));
        }
        return tokenPromise.then((r) => {
            tokenPromise = null;
            return r;
        });
    };
    /**
     * Requests user profile from facade
     * @type {getUserProfile}
     */
    const getUserProfile = () => {
        if (!profilePromise) {
            /** @type {AuthMessageOutgoing<AuthMessageType['PROFILE']>} */
            const msg = { type: AuthMessageType.PROFILE };
            profilePromise = emit(msg, { observer, emitter }).catch(() => Promise.resolve(null));
        }
        return profilePromise;
    };
    /**
     * Requests logout action from facade
     * @type {logout}
     */
    const logout = (idToken) => {
        /** @type {AuthMessageOutgoing<AuthMessageType['LOGOUT']>} */
        const msg = { type: AuthMessageType.LOGOUT, payload: idToken };
        return emit(msg, { observer, emitter }).catch(() => Promise.resolve());
    };

    /**
     * Checks whether facade is available
     * @returns {boolean}
     */
    const isAvailable = () => window.parent != null && window.parent !== window;

    return { updateToken, getUserProfile, logout, setup, isAvailable };
};

// {
//     const isUserAuthenticated = true;
//     const cb = [];
//     // emulates -> window.parent.postMessage(msg, '*')
//     /** @type {Emitter} */
//     const emitter = (msg) => {
//         const { type } = msg;
//         const payloads = {
//             [AuthMessageType.TOKEN]: isUserAuthenticated ? { id_token: '...', access_token: '...' } : null,
//             [AuthMessageType.PROFILE]: isUserAuthenticated ? { username: '...', email: '...' } : null
//         };
//         console.log('window.parent', 'onMessage:', msg);
//         // emulates -> (parent.window) iframe.contentWindow.postMessage(msg, '*');
//         cb.forEach((fn) => {
//             const msg = { type, payload: payloads[type] };
//             fn(msg);
//         });
//     };
//     // emulates -> window.addEventlistener('message', fn);
//     /** @type {Observer} */
//     const observer = (fn) => cb.push(fn);
//     const api = useIFrame();

//     // наш редактор через auth adapter дергает кучу раз синхронно
//     api.setup({ emitter, observer });
//     // эти 4 вызова должны слиться в 2
//     api.getUserProfile(); //?
//     api.getUserProfile(); //?
//     api.updateToken(); //?
//     api.updateToken(); //?
//     // а этот уже должен вызвать новый запрос
//     setTimeout(() => {
//         // наш редактор через auth adapter дергает асинхронно (клик -> загрузка данных и тд)
//         api.updateToken(); //?
//         api.logout('<id_token>'); //?
//     });
// }
